import { useState } from "react";
import { X } from "lucide-react";
import { Button, Icon, Modal, TrendSparkline } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useDashboardStats } from "../../app/hooks/useDashboardStats.js";
import { AnalyticsBox } from "./DashboardAnalytics.js";
import { PlatformBadge } from "./PlatformBadge.js";
import {
  trendingDayLabel,
  trendingDetail,
  trendingHeadline,
  trendingPctLabel,
  trendingWhenLabel,
} from "./trendingCopy.js";
import { platformLabel } from "./platforms.js";
import styles from "./Trending.module.scss";

type TrendingItem = components["schemas"]["TrendingItem"];

/**
 * "Trending" — the dashboard's daily SPIKE feed, and the card the design reference actually ships in the wide
 * main-column slot (it replaced "Biggest movers" there on 2026-07-07: "the daily spike feed is more valuable
 * than the total-ranked movers grid").
 *
 * Each pill is one detected spike: a metric that jumped hard enough, on a specific day, to be worth acting on —
 * NOT a ranking of who moved most. The distinction is the whole point of the card, and is why the Spotlight's
 * "top mover" is a separate computation (`trending.movers`) rather than this list's first row.
 *
 * Money-neutral: a spike is not financial, so there is no money gate. Born-blank renders the designed empty
 * state — never a placeholder row, never a fabricated percentage.
 */
export function TrendingCard() {
  const q = useDashboardStats();
  const items = q.data?.trending?.items ?? [];

  return (
    // The SAME shell as its siblings on this page (Streaming / Socials / Top Cities / Recent Release): it owns
    // the four-state contract and the count tag. No block-level as-of marker: "when data last refreshed" is not
    // what this card is about, and each pill already states the day its own spike happened.
    <AnalyticsBox
      id="trending"
      title="Trending"
      q={q}
      count={items.length}
      empty={items.length === 0}
      emptyTitle="Nothing trending yet"
      emptyDesc="Notable streaming and social movements show up here once your connections have a few days of history."
    >
      <TrendingPillList items={items} wide />
    </AnalyticsBox>
  );
}

/**
 * A Trending card's title with its count beside it — the artist dashboard's head, for the roster's Trending cards
 * (ART-334/332), which sit in a plain `Card`. The count is `aria-hidden`: the card's name stays "Trending".
 */
export function TrendingTitle({ count }: { count: number }) {
  return (
    <>
      Trending{count > 0 && <span className={styles.titleCount} aria-hidden="true">{count}</span>}
    </>
  );
}

/**
 * The pill's thumbnail, and the spec's THREE-RUNG chain: real artwork → the platform's brand glyph → a letter
 * tile. Artwork is rung one and lives here; {@link PlatformBadge} is both rungs beneath it.
 *
 * A broken image URL falls DOWN the chain rather than rendering a broken-image glyph — post thumbnails are
 * vendor CDN links that expire, so this is the ordinary case rather than the edge. The failed URL is what is
 * held in state, not a boolean, so a component re-used for a different item recovers on its own instead of
 * staying permanently fallen back.
 */
function TrendThumb({ item, size }: { item: TrendingItem; size: "md" | "lg" }) {
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const src = item.thumbnailUrl && item.thumbnailUrl !== brokenUrl ? item.thumbnailUrl : null;

  if (!src) {
    return <PlatformBadge platform={item.platform} size={size} shape={size === "md" ? "circle" : "square"} />;
  }
  return (
    <img
      className={styles.thumb}
      data-size={size}
      src={src}
      alt=""
      loading="lazy"
      onError={() => setBrokenUrl(src)}
    />
  );
}

/**
 * The spike pills, EXTRACTED so the roster's per-artist Trending card renders the same object this one does —
 * INCLUDING the detail modal, which is why the open state lives here rather than in the dashboard card. Hoisted
 * to the caller it was a prop the roster did not pass, so the roster's pills rendered `disabled`: the same
 * component, offering nothing when pressed.
 *
 * It is the wording that makes this worth sharing, not the markup. `TrendingItem.Subject` is a dedupe KEY
 * (`platform:spotify:followers_total`), never a label — the roster card printed it raw, so a spike read
 * "Platform:Tiktok:Followers_total" where the design says "This TikTok post saw a big spike in views".
 * `trendingCopy` is the one place that turns an item into English, and both surfaces read the same series,
 * so two renderings of it could only ever disagree.
 *
 * `wide` is the spec's one prop, not a second component: the dashboard's main-column slot lays the pills 2-up
 * and collapses to one column when the column narrows; the roster card and any rail take the default single
 * column.
 */
export function TrendingPillList({
  items,
  wide,
  artistOf,
}: {
  items: readonly TrendingItem[];
  wide?: boolean;
  /** Whose spike a pill is — leading its meta line and the pop-up's eyebrow, for a list spanning artists. */
  artistOf?: (item: TrendingItem) => string | null | undefined;
}) {
  const [open, setOpen] = useState<TrendingItem | null>(null);

  return (
    <>
      <ul className={styles.list} data-wide={wide ? "true" : undefined}>
        {items.map((it) => (
          <li key={`${artistOf?.(it) ?? ""}-${it.subject ?? `${it.platform}-${it.metric}-${it.detectedOn}`}`}>
            <button type="button" className={styles.pill} onClick={() => setOpen(it)}>
              <TrendThumb item={it} size="md" />
              <span className={styles.body}>
                <span className={styles.headline}>{trendingHeadline(it)}</span>
                <span className={styles.meta}>
                  {artistOf?.(it) ? `${artistOf(it)} · ` : ""}
                  {platformLabel(it.platform)} · {trendingWhenLabel(it.detectedOn)}
                </span>
              </span>
              {/* Signed, so direction is carried textually as well as by the tone — never colour alone. */}
              <span className={styles.pct}>{trendingPctLabel(it.pct)}</span>
            </button>
          </li>
        ))}
      </ul>

      {open && <TrendingDetailModal item={open} artist={artistOf?.(open)} onClose={() => setOpen(null)} />}
    </>
  );
}

/**
 * The spike detail.
 *
 * Uses the shared `Modal` primitive — which owns the focus trap, the Escape STACK and the body-scroll lock —
 * rather than a hand-rolled portal + keydown listener (the reference hand-rolls one; doing that here has cost
 * two live bugs). `hideHeader` because the spec's head is a LOCKUP the primitive's header cannot express:
 * thumbnail, then the platform/day line ABOVE a sentence-case heading, then the percentage chip. The title
 * survives as the dialog's sr-only accessible name, so `aria-labelledby` still resolves — the same shape the
 * profile dialogs use.
 */
function TrendingDetailModal({ item, artist, onClose }: {
  item: TrendingItem;
  /** The artist the spike is theirs — added to the eyebrow when the list spans artists (ART-335). */
  artist?: string | null;
  onClose: () => void;
}) {
  const d = trendingDetail(item);
  // The exact daily values detection ran on, so the graph can never disagree with the percentage beside it.
  const points = (item.series ?? [])
    .filter((p) => p.value != null)
    .map((p) => ({ label: trendingDayLabel(p.day), value: p.value as number }));

  return (
    <Modal title={d.heading} onClose={onClose} size="sm" hideHeader>
      <div className={styles.detailHead}>
        <TrendThumb item={item} size="lg" />
        <div className={styles.detailText}>
          <p className={styles.detailEyebrow}>
            {artist ? `${artist} · ` : ""}{platformLabel(item.platform)} · {trendingWhenLabel(item.detectedOn)}
          </p>
          {/* The dialog's own name is drawn here; `hideHeader` keeps a matching sr-only copy as the label, so
              this is the SECOND node carrying it — the same shape the two profile dialogs ship. Worth
              collapsing one day by teaching `Modal` to accept a caller-supplied `labelledBy`, which would fix
              all three at once; not worth a bespoke fix here. */}
          <h2 className={styles.detailTitle}>{d.heading}</h2>
        </div>
        <span className={styles.pct} data-lg="true">
          {trendingPctLabel(item.pct)}
        </span>
        <button type="button" className={styles.detailClose} aria-label="Close" onClick={onClose}>
          <Icon icon={X} size={20} />
        </button>
      </div>

      <p className={styles.detailBody}>{d.body}</p>

      {/* Fewer than two points renders NOTHING — the wrapper is conditional too, so an item detected before
          series capture existed leaves no phantom margin behind an absent chart. */}
      {points.length >= 2 && (
        <div className={styles.spark}>
          <TrendSparkline points={points} label={`${d.heading} — daily trend`} />
        </div>
      )}

      <div className={styles.detailActions}>
        {d.linkUrl ? (
          <a className={styles.detailLink} href={d.linkUrl} target="_blank" rel="noopener noreferrer">
            {d.linkLabel} ↗
          </a>
        ) : (
          <span />
        )}
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
